import { useEffect, useState } from 'react'
import { X } from 'lucide-react'
import Button from '../ui/Button'
import FormField from '../ui/FormField'
import Input from '../ui/Input'
import { ui } from '../../styles/ui'
import { useTranslation } from '../../contexts/TranslationContext'

export default function UserEditDialog({ open, user, saving = false, onCancel, onSave }) {
  const { t } = useTranslation()
  const [form, setForm] = useState({
    name: '',
    email: '',
    password: '',
    passwordConfirmation: '',
  })
  const [validationErrors, setValidationErrors] = useState({})
  const [submitError, setSubmitError] = useState('')

  useEffect(() => {
    if (!open) return
    setForm({
      name: user?.name || '',
      email: user?.email || '',
      password: '',
      passwordConfirmation: '',
    })
    setValidationErrors({})
    setSubmitError('')
  }, [open, user?.id, user?.name, user?.email])

  useEffect(() => {
    if (!open) return undefined

    const previousOverflow = document.body.style.overflow
    document.body.style.overflow = 'hidden'

    const handleKeyDown = (event) => {
      if (event.key === 'Escape') {
        event.preventDefault()
        onCancel?.()
      }
    }

    window.addEventListener('keydown', handleKeyDown)
    return () => {
      window.removeEventListener('keydown', handleKeyDown)
      document.body.style.overflow = previousOverflow
    }
  }, [open, onCancel])

  if (!open || !user) {
    return null
  }

  const setField = (field, value) => {
    setForm((prev) => ({ ...prev, [field]: value }))
    setValidationErrors((prev) => ({ ...prev, [field]: '', form: '' }))
    setSubmitError('')
  }

  const validate = () => {
    const errors = {}

    if (!form.name.trim()) {
      errors.name = t('Name is required', 'Name is required')
    }
    if (!form.email.trim()) {
      errors.email = t('Email is required', 'Email is required')
    }

    const passwordFilled = Boolean(form.password || form.passwordConfirmation)
    if (passwordFilled && !form.password) {
      errors.password = t('Password is required', 'Password is required')
    } else if (form.password && form.password.length < 8) {
      errors.password = t('Password must be at least 8 characters', 'Password must be at least 8 characters')
    }
    if (passwordFilled && !form.passwordConfirmation) {
      errors.passwordConfirmation = t('Please confirm the password', 'Please confirm the password')
    } else if (passwordFilled && form.password !== form.passwordConfirmation) {
      errors.passwordConfirmation = t('Passwords do not match', 'Passwords do not match')
    }

    return errors
  }

  const handleSubmit = async (event) => {
    event.preventDefault()
    const errors = validate()
    setValidationErrors(errors)
    if (Object.keys(errors).length > 0) {
      return
    }

    setSubmitError('')
    try {
      await onSave?.({
        name: form.name.trim(),
        email: form.email.trim(),
        password: form.password ? form.password : undefined,
        password_confirmation: form.password ? form.passwordConfirmation : undefined,
      })
    } catch (error) {
      setSubmitError(error.response?.data?.message || error.message || t('Failed to update user', 'Failed to update user'))
    }
  }

  return (
    <div
      className="fixed inset-0 z-50 overflow-y-auto bg-slate-950/60 px-4 py-8 backdrop-blur-sm"
      role="presentation"
      onMouseDown={(event) => {
        if (event.target === event.currentTarget) {
          onCancel?.()
        }
      }}
    >
      <div
        className="mx-auto flex w-full flex-col overflow-hidden rounded-3xl border border-outline bg-surface shadow-2xl ring-1 ring-black/10"
        style={{ maxWidth: '56rem', maxHeight: 'calc(100vh - 4rem)' }}
        role="dialog"
        aria-modal="true"
        aria-labelledby="user-edit-dialog-title"
        aria-describedby="user-edit-dialog-description"
        onMouseDown={(event) => event.stopPropagation()}
      >
        <div className="flex items-start justify-between gap-4 border-b border-white/10 bg-gradient-to-r from-slate-950 via-slate-900 to-slate-950 px-6 py-5 text-white">
          <div className="min-w-0">
            <div className="flex flex-wrap items-center gap-2">
              <span className="rounded-full bg-white/10 px-2.5 py-1 text-xs font-medium tracking-wide text-white/80">
                {t('User profile', 'User profile')}
              </span>
              <span className="rounded-full bg-sky-500/15 px-2.5 py-1 text-xs font-medium text-sky-100 ring-1 ring-inset ring-sky-400/30">
                {user.is_admin ? t('Admin', 'Admin') : t('User', 'User')}
              </span>
            </div>
            <h2 id="user-edit-dialog-title" className="mt-3 text-2xl font-semibold tracking-tight text-white">
              {t('Edit User', 'Edit User')}
            </h2>
            <p id="user-edit-dialog-description" className="mt-2 max-w-2xl text-sm text-slate-300">
              {t('Update the user name, email, or password. Leave password blank to keep it unchanged.', 'Update the user name, email, or password. Leave password blank to keep it unchanged.')}
            </p>
            <div className="mt-4 flex flex-wrap gap-2 text-xs text-slate-300">
              <span className="rounded-full bg-white/5 px-3 py-1 ring-1 ring-inset ring-white/10">
                {t('ID', 'ID')}: {user.id}
              </span>
              <span className="rounded-full bg-white/5 px-3 py-1 ring-1 ring-inset ring-white/10">
                {user.email}
              </span>
            </div>
          </div>
          <Button
            type="button"
            variant="icon"
            onClick={() => onCancel?.()}
            aria-label={t('Close edit dialog', 'Close edit dialog')}
            className="shrink-0 text-white/80 hover:bg-white/10 hover:text-white focus:ring-white/40"
          >
            <X size={18} />
          </Button>
        </div>

        <form onSubmit={handleSubmit} className="min-h-0 flex-1 overflow-y-auto bg-surface px-6 py-6">
          {submitError ? (
            <div className={`${ui.alertError} mb-4`}>{submitError}</div>
          ) : null}

          <div className="space-y-6">
            <section className="rounded-2xl border border-outline bg-surface-elevated/50 p-5">
              <div className="mb-4">
                <h3 className="text-sm font-semibold uppercase tracking-[0.18em] text-muted-foreground">
                  {t('Profile details', 'Profile details')}
                </h3>
                <p className="mt-1 text-sm text-muted-foreground">
                  {t('Core identity and contact information for the account.', 'Core identity and contact information for the account.')}
                </p>
              </div>

              <div className="grid gap-4 md:grid-cols-2">
                <FormField label={t('Name', 'Name')} htmlFor="edit-user-name" error={validationErrors.name}>
                  <Input
                    id="edit-user-name"
                    value={form.name}
                    onChange={(event) => setField('name', event.target.value)}
                    aria-invalid={Boolean(validationErrors.name)}
                  />
                </FormField>

                <FormField label={t('Email', 'Email')} htmlFor="edit-user-email" error={validationErrors.email}>
                  <Input
                    id="edit-user-email"
                    type="email"
                    value={form.email}
                    onChange={(event) => setField('email', event.target.value)}
                    aria-invalid={Boolean(validationErrors.email)}
                  />
                </FormField>
              </div>
            </section>

            <section className="rounded-2xl border border-outline bg-surface-elevated/50 p-5">
              <div className="mb-4 flex flex-wrap items-start justify-between gap-3">
                <div>
                  <h3 className="text-sm font-semibold uppercase tracking-[0.18em] text-muted-foreground">
                    {t('Security', 'Security')}
                  </h3>
                  <p className="mt-1 text-sm text-muted-foreground">
                    {t('Set a new password only when needed. Leaving it blank preserves the current one.', 'Set a new password only when needed. Leaving it blank preserves the current one.')}
                  </p>
                </div>
                <span className="rounded-full border border-amber-200 bg-amber-50 px-3 py-1 text-xs font-medium text-amber-800">
                  {t('Password optional', 'Password optional')}
                </span>
              </div>

              <div className="grid gap-4 md:grid-cols-2">
                <FormField
                  label={t('New password', 'New password')}
                  htmlFor="edit-user-password"
                  error={validationErrors.password}
                  helpText={t('Optional. Leave blank to keep the current password.', 'Optional. Leave blank to keep the current password.')}
                >
                  <Input
                    id="edit-user-password"
                    type="password"
                    autoComplete="new-password"
                    value={form.password}
                    onChange={(event) => setField('password', event.target.value)}
                    aria-invalid={Boolean(validationErrors.password)}
                  />
                </FormField>

                <FormField
                  label={t('Confirm password', 'Confirm password')}
                  htmlFor="edit-user-password-confirmation"
                  error={validationErrors.passwordConfirmation}
                >
                  <Input
                    id="edit-user-password-confirmation"
                    type="password"
                    autoComplete="new-password"
                    value={form.passwordConfirmation}
                    onChange={(event) => setField('passwordConfirmation', event.target.value)}
                    aria-invalid={Boolean(validationErrors.passwordConfirmation)}
                  />
                </FormField>
              </div>

              <div className="mt-4 rounded-xl border border-sky-200 bg-sky-50 px-4 py-3 text-sm text-sky-900">
                {t("Changing the password will revoke the user's active sessions.", "Changing the password will revoke the user's active sessions.")}
              </div>
            </section>
          </div>

          <div className="mt-6 flex items-center justify-end gap-2 border-t border-divider bg-surface/90 pt-4">
            <Button type="button" variant="secondary" onClick={() => onCancel?.()}>
              {t('Cancel', 'Cancel')}
            </Button>
            <Button type="submit" variant="primary" disabled={saving}>
              {saving ? t('Saving...', 'Saving...') : t('Save changes', 'Save changes')}
            </Button>
          </div>
        </form>
      </div>
    </div>
  )
}
